<template>
  <div class="dialog">
    <span class="cls" @click="oth_showClaimExList(false)"></span>
    <div class="paper">
      <!-- Header -->
      <div class="header">
        <h2>청구사례</h2>
        <span class="close" @click="oth_showClaimExList(false)">
          <i class="ph ph-x"></i>
        </span>
      </div>

      <!-- Search & Sort -->
      <div class="search-bar">
        <div class="search-input">
          <i class="ph ph-magnifying-glass"></i>
          <input 
            type="text" 
            v-model="searchKeyword" 
            placeholder="사례명 / 질병명 검색"
            @keyup.enter="search"
          />
        </div>
        <button class="sort-btn" @click="toggleSortMenu">
          <i class="ph ph-funnel"></i>
        </button>
        <!-- Sort Dropdown -->
        <div class="sort-menu" v-if="showSortMenu">
          <div 
            class="sort-option" 
            v-for="option in sortOptions" 
            :key="option.value"
            :class="{ active: activeSort === option.value }"
            @click="changeSort(option.value)"
          >
            {{ option.label }}
          </div>
        </div>
      </div>

      <!-- Category Filter -->
      <div class="category-bar">
        <button 
          v-for="cat in categories" 
          :key="cat.value"
          :class="['category-btn', { active: activeCategory === cat.value }]"
          @click="changeCategory(cat.value)"
        >
          {{ cat.label }}
        </button>
      </div>

      <!-- Post Grid -->
      <div class="content">
        <div class="post-grid">
          <div 
            class="post-card" 
            :class="{ 'no-image': !getFirstImage(post.images) }"
            v-for="post in postList" 
            :key="post.idx"
            @click="openView(post.idx)"
          >
            <!-- 이미지 썸네일 -->
            <div class="card-thumb" v-if="getFirstImage(post.images)">
              <img :src="getFirstImage(post.images)" alt="썸네일" />
              <span class="image-count" v-if="getImageCount(post.images) > 1">
                <i class="ph ph-images"></i>
                {{ getImageCount(post.images) }}
              </span>
            </div>
            <div class="card-body">
              <div class="card-category">{{ getCategoryLabel(post.category) }}</div>
              <h4 class="card-title">{{ post.title }}</h4>
              <p class="card-preview">{{ post.content }}</p>
              <div class="card-meta">
                <span class="meta-item">
                  <i class="ph ph-heart"></i>
                  {{ post.like_count || 0 }}
                </span>
                <span class="meta-item">
                  <i class="ph ph-chat-circle"></i>
                  {{ post.comment_count || 0 }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="empty-state" v-if="postList.length === 0">
          <i class="ph ph-clipboard-text"></i>
          <p>등록된 사례가 없습니다</p>
        </div>

        <!-- Load More -->
        <div class="load-more" v-if="hasMore" ref="loadMoreRef">
          <span class="loading-spinner" v-if="isLoading"></span>
        </div>
      </div>

      <!-- Write FAB -->
      <button class="fab-write" @click="openEdit()">
        <i class="ph ph-plus"></i>
      </button>
    </div>

    <!-- Edit Dialog (새글 작성용) -->
    <ClaimExEdit 
      v-if="showEdit" 
      :oth_showClaimExEdit="toggleEdit"
      :editIdx="editIdx"
    />

    <!-- View Dialog (Edit는 View 내부에서 관리) -->
    <ClaimExView 
      v-if="showView" 
      :oth_showClaimExView="toggleView"
      :postIdx="viewIdx"
    />
  </div>
</template>

<script>
import { ref, inject, onMounted, onUnmounted } from "vue"
import ClaimExEdit from "@/components/dialogs/claimExEdit.vue"
import ClaimExView from "@/components/dialogs/claimExView.vue"

export default {
  components: { ClaimExEdit, ClaimExView },
  props: {
    oth_showClaimExList: Function
  },
  setup() {
    const sql_query = inject('sql_query')

    // 카테고리
    const categories = [
      { label: '전체', value: 'all' },
      { label: '암', value: 'cancer' },
      { label: '자동차', value: 'car' },
      { label: '3대질병', value: 'major3' },
      { label: '실손', value: 'actual' },
      { label: '기타', value: 'etc' }
    ]
    const activeCategory = ref('all')

    // 정렬
    const sortOptions = [
      { label: '최신순', value: 'recent' },
      { label: '인기순', value: 'popular' },
      { label: '댓글순', value: 'comments' }
    ]
    const activeSort = ref('recent')
    const showSortMenu = ref(false)

    const searchKeyword = ref('')
    const postList = ref([])
    
    // 무한스크롤
    const currentPage = ref(1)
    const perPage = 10
    const hasMore = ref(true)
    const isLoading = ref(false)
    const loadMoreRef = ref(null)
    let observer = null

    // 다이얼로그 상태
    const showEdit = ref(false)
    const editIdx = ref(null)
    const showView = ref(false)
    const viewIdx = ref(null)

    // 카테고리 라벨 가져오기
    const getCategoryLabel = (value) => {
      const cat = categories.find(c => c.value === value)
      return cat ? cat.label : '기타'
    }

    // 첫 번째 이미지 가져오기
    const getFirstImage = (imagesJson) => {
      if (!imagesJson) return null
      try {
        const images = JSON.parse(imagesJson)
        return images.length > 0 ? images[0] : null
      } catch {
        return null
      }
    }

    // 이미지 개수 가져오기
    const getImageCount = (imagesJson) => {
      if (!imagesJson) return 0
      try {
        const images = JSON.parse(imagesJson)
        return images.length
      } catch {
        return 0
      }
    }

    // 게시글 목록 조회
    const getPostList = async (reset = false) => {
      if (isLoading.value) return
      isLoading.value = true

      if (reset) {
        currentPage.value = 1
        postList.value = []
        hasMore.value = true
      }

      let orderBy = 'created_at DESC'
      if (activeSort.value === 'popular') {
        orderBy = 'like_count DESC, created_at DESC'
      } else if (activeSort.value === 'comments') {
        orderBy = 'comment_count DESC, created_at DESC'
      }

      let whereClause = '1=1'
      if (activeCategory.value !== 'all') {
        whereClause += ` AND category = '${activeCategory.value}'`
      }
      if (searchKeyword.value.trim()) {
        const keyword = searchKeyword.value.replace(/'/g, "''")
        whereClause += ` AND (title LIKE '%${keyword}%' OR content LIKE '%${keyword}%')`
      }

      const offset = (currentPage.value - 1) * perPage
      
      try {
        const rows = await sql_query(`
          SELECT idx, mb_id, title, content, category, images, like_count, comment_count, created_at
          FROM claim_ex_post
          WHERE ${whereClause}
          ORDER BY ${orderBy}
          LIMIT ${perPage} OFFSET ${offset}
        `)

        if (!rows || rows.length < perPage) {
          hasMore.value = false
        }

        postList.value = [...postList.value, ...(rows || [])]
        currentPage.value++
      } catch (e) {
        console.error('게시글 조회 오류:', e)
      } finally {
        isLoading.value = false
      }
    }

    const changeCategory = (cat) => {
      activeCategory.value = cat
      getPostList(true)
    }

    const changeSort = (sort) => {
      activeSort.value = sort
      showSortMenu.value = false
      getPostList(true)
    }

    const toggleSortMenu = () => {
      showSortMenu.value = !showSortMenu.value
    }

    const search = () => {
      getPostList(true)
    }

    // Edit 다이얼로그 (새글 작성용)
    const toggleEdit = (show, options = {}) => {
      if (show) {
        editIdx.value = options.editIdx || null
        showEdit.value = true
      } else {
        showEdit.value = false
        editIdx.value = null
        if (options.refresh) {
          getPostList(true)
        }
      }
    }

    // View 다이얼로그
    const toggleView = (show, options = {}) => {
      if (show) {
        viewIdx.value = options.postIdx
        showView.value = true
      } else {
        showView.value = false
        viewIdx.value = null
        if (options.refresh) {
          getPostList(true)
        }
      }
    }

    const openEdit = (idx = null) => {
      toggleEdit(true, { editIdx: idx })
    }

    const openView = (idx) => {
      toggleView(true, { postIdx: idx })
    }

    // Intersection Observer for infinite scroll
    const setupObserver = () => {
      observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore.value && !isLoading.value) {
          getPostList()
        }
      }, { threshold: 0.1 })

      if (loadMoreRef.value) {
        observer.observe(loadMoreRef.value)
      }
    }

    // 외부 클릭 시 정렬 메뉴 닫기
    const handleClickOutside = (e) => {
      if (!e.target.closest('.sort-btn') && !e.target.closest('.sort-menu')) {
        showSortMenu.value = false
      }
    }

    onMounted(() => {
      getPostList()
      setTimeout(setupObserver, 100)
      document.addEventListener('click', handleClickOutside)
    })

    onUnmounted(() => {
      if (observer) observer.disconnect()
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      categories,
      activeCategory,
      sortOptions,
      activeSort,
      showSortMenu,
      searchKeyword,
      postList,
      hasMore,
      isLoading,
      loadMoreRef,
      showEdit,
      editIdx,
      showView,
      viewIdx,
      getCategoryLabel,
      getFirstImage,
      getImageCount,
      changeCategory,
      changeSort,
      toggleSortMenu,
      search,
      toggleEdit,
      toggleView,
      openEdit,
      openView
    }
  }
}
</script>

<style scoped>
.dialog {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: var(--z-index-middle);
  backdrop-filter: blur(var(--effect-blur));
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  position: relative;
  width: 90%;
  max-width: 600px;
  height: 85%;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-lg);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  position: relative;
  flex-shrink: 0;
  border-bottom: 1px solid var(--color-background-light);
}

.header > h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0;
}

.header > .close {
  position: absolute;
  right: 16px;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.header > .close > i {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
}

/* Search Bar */
.search-bar {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  flex-shrink: 0;
  position: relative;
}

.search-input {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  background: var(--color-background-mute);
}

.search-input > i {
  color: var(--color-text-third);
  font-size: var(--font-size-lg);
}

.search-input > input {
  flex: 1;
  border: none;
  background: none;
  padding: 10px 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  outline: none;
}

.search-input > input::placeholder {
  color: var(--color-text-third);
}

.sort-btn {
  width: 42px;
  height: 42px;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  background: var(--color-background-mute);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sort-btn > i {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
}

.sort-menu {
  position: absolute;
  top: 100%;
  right: 16px;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 10;
}

.sort-option {
  padding: 12px 20px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  cursor: pointer;
  white-space: nowrap;
}

.sort-option:hover {
  background: var(--color-background-mute);
}

.sort-option.active {
  color: var(--color-brand);
  font-weight: var(--font-weight-medium);
}

/* Category Bar */
.category-bar {
  display: flex;
  gap: 8px;
  padding: 0 16px 12px;
  flex-shrink: 0;
  overflow-x: auto;
}

.category-bar::-webkit-scrollbar {
  display: none;
}

.category-btn {
  padding: 6px 14px;
  border: 1px solid var(--color-background-light);
  border-radius: 20px;
  background: var(--color-background);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.category-btn.active {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: var(--color-text-white);
}

/* Content */
.content {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 80px;
}

/* Post Grid */
.post-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.post-card {
  background: var(--color-background-mute);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;
}

.post-card:active {
  transform: scale(0.98);
}

.card-thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  background: var(--color-background-light);
}

.card-thumb > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-thumb > .image-count {
  position: absolute;
  right: 6px;
  bottom: 6px;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 3px 6px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  font-size: 10px;
  color: #fff;
}

.card-thumb > .image-count > i {
  font-size: 12px;
}

.card-body {
  padding: 12px;
}

.post-card.no-image .card-body {
  padding-top: 14px;
}

.card-category {
  display: inline-block;
  padding: 3px 8px;
  background: var(--color-brand-light);
  border-radius: 4px;
  font-size: 10px;
  font-weight: var(--font-weight-medium);
  color: var(--color-brand);
  margin-bottom: 8px;
}

.card-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin: 0 0 6px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-preview {
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
  margin: 0 0 10px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-meta {
  display: flex;
  gap: 12px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: var(--color-text-third);
}

.meta-item > i {
  font-size: var(--font-size-sm);
}

/* Empty State */
.empty-state {
  padding: 60px 0;
  text-align: center;
}

.empty-state > i {
  font-size: 48px;
  color: var(--color-text-third);
  margin-bottom: 12px;
}

.empty-state > p {
  color: var(--color-text-secondary);
  margin: 0;
  font-size: var(--font-size-sm);
}

/* Load More */
.load-more {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--color-background-light);
  border-top-color: var(--color-brand);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* FAB Write Button */
.fab-write {
  position: absolute;
  right: 20px;
  bottom: 20px;
  width: 52px;
  height: 52px;
  border: none;
  border-radius: 50%;
  background: var(--color-brand);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
}

.fab-write:active {
  transform: scale(0.95);
}

.fab-write > i {
  font-size: 24px;
  color: var(--color-text-white);
}
</style>